import { Project, User } from "@prisma/client";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { ApiCo2OffsettingReturnValue } from "../../pages/api/co2offsetting";

import { CodeProjectUserRelation, UserApi } from "../util/prismaHelper";
import {
  PaginatedQuery,
  QuerySearch,
  PaginatedResponse,
} from "../util/utilServer";

// Create our baseQuery instance
const baseQuery = fetchBaseQuery({
  baseUrl: "/api/",
});

const api = createApi({
  reducerPath: "api",
  baseQuery: baseQuery,
  endpoints: () => ({}),
  tagTypes: [
    "Co2Offsetting",
    "CodeProjectRelation",
    "ProjectList",
    "Settings",
    "User",
    "User.Me",
  ],
});

export const ResetApi = api.util.resetApiState;

const statApi = api.injectEndpoints({
  endpoints: (build) => ({
    getSettings: build.query<any, void>({
      query: () => ({
        url: `settings`,
        method: "GET",
      }),
      providesTags: ["Settings"],
    }),
    updateSettings: build.mutation<any, Partial<any>>({
      query: (settings) => ({
        url: `settings`,
        method: "PUT",
        body: settings,
      }),
      invalidatesTags: ["Settings"],
    }),
    getUser: build.query<UserApi, string>({
      query: (id) => ({ url: `/user/${id}`, method: "GET" }),
      providesTags: ["User"],
    }),
    getCurrentUser: build.query<UserApi, void>({
      query: () => ({ url: "/user/me", method: "GET" }),
      providesTags: ["User.Me"],
    }),
    updateUser: build.mutation<UserApi, FormData | Partial<User>>({
      query: ({ ...args }) => ({
        url: `/user`,
        method: "POST",
        body: args,
      }),
      invalidatesTags: ["User", "User.Me"],
    }),
    addCo2Code: build.mutation<
      { success: boolean; treeIds: number[] },
      { code: string }
    >({
      query: (arg) => ({
        url: "/code",
        method: "post",
        body: arg,
      }),
      invalidatesTags: ["Co2Offsetting"],
    }),
    getCo2Offsetting: build.query<
      PaginatedResponse<ApiCo2OffsettingReturnValue>,
      PaginatedQuery["page"]
    >({
      query: (page = 1) => ({
        url: `/co2offsetting?page=${page}`,
        method: "GET",
      }),
      providesTags: ["Co2Offsetting"],
    }),
    getProjects: build.query<PaginatedResponse<Project[]>, QuerySearch>({
      query: (query) => ({
        url: `/project?${new URLSearchParams(query).toString()}`,
        method: "GET",
      }),
      providesTags: ["ProjectList"],
    }),
    addProject: build.mutation<
      { success: true; projectId: string } | { error: string },
      FormData
    >({
      query: (treeData) => ({
        url: `/project`,
        method: "POST",
        body: treeData,
      }),
      invalidatesTags: ["ProjectList"],
    }),
    deleteGAConnection: build.mutation<void, void>({
      query: () => ({
        url: `/connection`,
        method: "DELETE",
      }),
      invalidatesTags: ["User"],
    }),
    listCodeProjectRelations: build.query<
      PaginatedResponse<CodeProjectUserRelation[]>,
      PaginatedQuery & { type: "nft" | "project"; id: string }
    >({
      query: ({ id, page = 1, type }) => ({
        url:
          (type === "nft" ? "nft/" : "project/") + id + "/codes?page=" + page,
        method: "GET",
      }),
      providesTags: (result, error, arg) => [
        { type: "CodeProjectRelation", id: arg.id },
      ],
    }),
  }),
});

export const ApiReducer = api.reducer,
  ApiMiddleware = api.middleware,
  ApiReducerPath = api.reducerPath;
export const {
  useGetCurrentUserQuery,
  useGetCo2OffsettingQuery,
  useAddCo2CodeMutation,
  useUpdateUserMutation,
  useGetProjectsQuery,
  useAddProjectMutation,
  useDeleteGAConnectionMutation,
  useListCodeProjectRelationsQuery,
} = statApi;
export const _statApi = statApi;
